import React, { useState, useEffect, useRef, useLayoutEffect } from "react";
import { connect } from "react-redux";

import { Avatar } from "@material-ui/core";
import EqualizerIcon from "@material-ui/icons/Equalizer";
import EventLabel2, { init } from "../../../components/EventLabel2/EventLabel2";
import { WordCloud } from "../../../interaction/presenter/other/WordCloud/WordCloud";
import { ColumnChart } from "../../../interaction/presenter/charts/ColumnChart/ColumnChart";
import { BarChart } from "../../../interaction/presenter/charts/BarChart/BarChart";
import { PieChart } from "../../../interaction/presenter/charts/PieChart/PieChart";
import { presenterTypeConstants } from "../../../interaction/constants/presenterType.constants";
import { DisparityChart } from "../../../interaction/presenter/charts/DisparityChart/DisparityChart";
import { TextFlow } from "../../../interaction/presenter/other/TextFlow/TextFlow";
import { useMemo } from "react";

const convertOptionIdToText = (id, qs, lang) => {
  let optionId = parseInt(id);
  if (isNaN(optionId)) return id;
  let text = id;
  try {
    text = qs.questionOption.find((o) => o.questionOptionId === optionId).questionOptionLanguage.find((l) => l.languageCode === lang).label;
  } catch (error) { }
  return text;
};

const defaultState = {
  activeQuestion: null,
  clientResponses: [],
  presentParticipants: [],
  presentationOrder: "SORTORDER",
  displayType: "",
  presenterSettings: {
    displayTypeText: "TEXTFLOW",
    displayTypeOption: "BARCHART",
    moderationMode: false,
    presentationOrder: "SORTORDER",
    showCount: true,
    showPercent: false,
  },
  size: null,
};
function PresenterModule(props) {
  const [state, setState] = useState({ ...defaultState });
  const wrapperRef = useRef();
  const stateRef = useRef();
  stateRef.current = state;
  const { eventId, lang, hubConnection, preview } = props;
  useEffect(() => {
    init(eventId, "DigitalMeeting.Presenter", lang);
    if (hubConnection && !preview) {
      hubConnection.invoke("getClientQuestions", eventId, "INTERACTION_PRESENTER");
      hubConnection.invoke("getPresenterSettings", eventId);
    }
    return () => { };
  }, []);
  useEffect(() => {
    window.addEventListener("resize", resizeGraph);
    if (hubConnection && !preview) {
      hubConnection.on("onDeployQuestionToPresenter", onDeployQuestionToPresenter);
      hubConnection.on("onNewPresentParticipant", onNewPresentParticipant);
      hubConnection.on("onUpdatedPresenterSettings", onUpdatedPresenterSettings);
      hubConnection.on("onClearDisplay", onClearDisplay);
      hubConnection.on("onClearQuestionAnswers", onClearQuestionAnswers);
      hubConnection.on("onClientResponse", onClientResponse);
    }
    return () => {
      if (hubConnection && !preview) {
        hubConnection.off("onDeployQuestionToPresenter", onDeployQuestionToPresenter);
        hubConnection.off("onNewPresentParticipant", onNewPresentParticipant);
        hubConnection.off("onUpdatedPresenterSettings", onUpdatedPresenterSettings);
        hubConnection.off("onClearDisplay", onClearDisplay);
        hubConnection.off("onClearQuestionAnswers", onClearQuestionAnswers);
        hubConnection.off("onClientResponse", onClientResponse);
      }
      window.removeEventListener("resize", resizeGraph);
    };
  });
  useEffect(() => {
    if ((wrapperRef.current && !state.size) || (wrapperRef.current && wrapperRef.current.clientHeight != state.size.height && wrapperRef.current.clientWidth != state.size.width)) {
      resizeGraph();
    }
  }, [wrapperRef.current]);

  const resizeGraph = () => {
    if (wrapperRef.current) {
      const { clientWidth, clientHeight } = wrapperRef.current;
      setState({
        ...stateRef.current,
        size: {
          height: clientHeight,
          width: clientWidth,
        },
      });
    }
  };
  const onClientResponse = (response) => {
    const { clientResponses, activeQuestion } = stateRef.current;
    let responses = Array.from(
      clientResponses.map((a) => {
        if (a.type === "Option") {
          a.answerText = convertOptionIdToText(a.answer);
        }
        return a;
      })
    );
    const existingResponse = responses.find((r) => {
      return r.answerId === response.answerId;
    });
    if (existingResponse && response.status === 3) {
      responses.splice(responses.indexOf(existingResponse), 1);
    }
    if (response.type === "Option") {
      response.answerText = convertOptionIdToText(response.answer);
    }
    if (!existingResponse) {
      responses.push(response);
    }
    if (response && activeQuestion && response.questionId === activeQuestion.questionId) {
      setState({ ...stateRef.current, clientResponses: responses });
    }
  };
  const onNewPresentParticipant = (order) => {
    const { presentParticipants } = stateRef.current;
    if (!presentParticipants.find((x) => x.badgeNo == order.badgeNo)) {
      presentParticipants.push(order);
      setState({ ...stateRef.current, presentParticipants });
    }
  };
  function onDeployQuestionToPresenter(res) {
    // let presenterType = res.question.presenterType || (res.question.type == "Text" ? "WORDCLOUD" : "COLUMNCHART");
    setState({
      ...stateRef.current,
      clientResponses: res.answers,
      activeQuestion: res.question,
      presentParticipants: res.presentParticipants || [],
      // displayType: presenterType,
    });
  }
  function onClearQuestionAnswers(questionId) {
    if (stateRef.current.activeQuestion && stateRef.current.activeQuestion.questionId == questionId) {
      setState({ ...stateRef.current, clientResponses: [] });
    }
  }
  function onClearDisplay() {
    setState({
      ...stateRef.current,
      activeQuestion: null,
      clientResponses: [],
    });
  }
  function onUpdatedPresenterSettings(settings) {
    setState({ ...stateRef.current, presenterSettings: settings });
  }
  const api = {
    ...state,
    ...props,
  };
  return (
    <div className="wh-100 bg-w" ref={(r) => (wrapperRef.current = r)}>
      {preview ? <FallbackChart /> : <Chart {...api} />}
    </div>
  );
}
function mapStateToProps(state) {
  return {
    template: state.template.template ? state.template.template.content : undefined,
  };
}
export default connect(mapStateToProps)(PresenterModule);

const setCategories = (question, lang) => {
  let categories = [];
  try {
    categories = question.questionOption.map((qo) => {
      return qo.questionOptionLanguage.find((ql) => ql.languageCode == lang).label || qo.questionOptionLanguage[0].label;
    });
  } catch (error) { }
  return categories;
};

const FallbackChart = () => {
  return (
    <div style={{ width: "100%", height: "100%", position: "relative" }}>
      <div className="centered-text">
        <Avatar style={{ margin: "auto" }}>
          <EqualizerIcon />
        </Avatar>
        <div style={{ marginTop: 10 }}>
          <EventLabel2 groupName="DigitalMeeting.Presenter" name="NoQuestionPresented" />
        </div>
      </div>
    </div>
  );
};

const Chart = (props) => {
  const { clientResponses, presenterSettings, activeQuestion, template, lang, size, presentParticipants, orderList, settings } = props;
  if (!activeQuestion || !size || !template || !presenterSettings) return <FallbackChart />;
  const { presentationOrder, showPercent, showCount, showNamesTextflow } = presenterSettings;
  const { showLabel } = settings;
  const questionType = activeQuestion.type;
  const categories = setCategories(activeQuestion, lang);
  const groupedTemplate = template;
  const presenterType = presenterSettings["displayType" + questionType];
  switch (presenterType) {
    case presenterTypeConstants.WORDCLOUD:
      return (
        <WordCloud
          data={clientResponses.map((a) => {
            return a.answer;
          })}
          size={size}
          autoFit={true}
          questionName={activeQuestion.questionLanguage.find((ql) => ql.languageCode === lang).label}
          template={groupedTemplate}
        />
      );
    case presenterTypeConstants.TEXTFLOW:
      return (
        <TextFlow
        data={clientResponses.map((a) => {
          return {answer: a.answer, name:a.clientName};
        })}
          questionName={activeQuestion.questionLanguage.find((ql) => ql.languageCode === lang).label}
          template={groupedTemplate}
          isModule={true}
          showLabel={showLabel}
          showNames={showNamesTextflow}
        />
      );
    case presenterTypeConstants.BARCHART:
      return (
        <BarChart
          data={clientResponses.map((a) => {
            let answer = convertOptionIdToText(a.answer, activeQuestion, lang);

            return { answer, weight: a.answerWeight };
          })}
          size={size}
          autoFit={true}
          categories={categories}
          showPercent={showPercent}
          showCount={showCount}
          questionName={activeQuestion.questionLanguage.find((ql) => ql.languageCode === lang).label}
          template={groupedTemplate}
          presentationOrder={presentationOrder}
        />
      );
    case presenterTypeConstants.COLUMNCHART:
      return (
        <ColumnChart
          data={clientResponses.map((a) => {
            let answer = convertOptionIdToText(a.answer, activeQuestion, lang);
            return { answer, weight: a.answerWeight };
          })}
          size={size}
          autoFit={true}
          showPercent={showPercent}
          showCount={showCount}
          categories={categories}
          questionName={activeQuestion.questionLanguage.find((ql) => ql.languageCode === lang).label}
          template={groupedTemplate}
          presentationOrder={presentationOrder}
        />
      );
    case presenterTypeConstants.DISPARITYCHART:
      return (
        <DisparityChart
          presentParticipants={presentParticipants}
          question={activeQuestion}
          questionName={activeQuestion.questionLanguage.find((ql) => ql.languageCode === lang).label}
          importedAttendees={orderList}
          clientResponses={clientResponses}
          template={groupedTemplate}
          autoFit={true}
        />
      );
    case presenterTypeConstants.PIECHART:
      return (
        <PieChart
          data={clientResponses.map((a) => {
            let answer = convertOptionIdToText(a.answer, activeQuestion, lang);
            return { answer, weight: a.answerWeight };
          })}
          size={size}
          categories={categories}
          showPercent={showPercent}
          showCount={showCount}
          presentationOrder={presentationOrder}
          autoFit={true}
          questionName={activeQuestion.questionLanguage.find((ql) => ql.languageCode === lang).label}
          template={groupedTemplate}
        />
      );

    default:
      return <FallbackChart />;
  }
};
